import React, { type FormEvent, useCallback, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import Select from '@atlaskit/select';
import Input from '@atlaskit/textfield';
import { useIntl } from '@atlassian/jira-intl';
import { TimeUnits } from '../../common/constants.tsx';
import type { TimeInputProps, OptionValue, DateTimeValue } from '../../common/types.tsx';
import Components from '../../common/ui/select-components/index.tsx';
import { StyledRow } from '../../common/ui/styled.tsx';
import messages, { messageLookup } from './messages.tsx';

const TimeInput = ({
	value: { value1, value2, excludeOverdue },
	onChange,
	typeConfig: { endLabel, overduePicker },
	isInvalid,
}: TimeInputProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [overdueOptions] = useState([
		{
			label: formatMessage(messages.labelOrIs),
			value: 'or-is',
		},
		{
			label: formatMessage(messages.labelAndNot),
			value: 'and-not',
		},
	]);
	const [options] = useState<OptionValue[]>(
		Object.entries(TimeUnits).map(([value, timeUnit]) => {
			const messageDescriptor = messageLookup[`${timeUnit}${endLabel || ''}`];
			return {
				label: messageDescriptor ? formatMessage(messageDescriptor) : '',
				value,
			};
		}),
	);

	useEffect(() => {
		if (value2 === '') {
			const value: DateTimeValue = {
				value1,
				value2: Object.keys(TimeUnits)[0],
			};

			if (overduePicker !== undefined && overduePicker === true) {
				value.excludeOverdue = excludeOverdue;
			}

			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onChange(value);
		}
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onChangeValue1 = useCallback(
		(event: FormEvent<HTMLInputElement>) => {
			const value: DateTimeValue = {
				value1: event.currentTarget.value,
				value2,
			};

			if (overduePicker !== undefined && overduePicker === true) {
				value.excludeOverdue = excludeOverdue;
			}

			onChange(value);
		},
		[value2, overduePicker, onChange, excludeOverdue],
	);

	const onChangeValue2 = useCallback(
		(optionValue: OptionValue | null) => {
			if (!optionValue) {
				return;
			}
			const value: DateTimeValue = {
				value1,
				value2: optionValue.value,
			};

			if (overduePicker !== undefined && overduePicker === true) {
				value.excludeOverdue = excludeOverdue;
			}

			onChange(value);
		},
		[excludeOverdue, onChange, overduePicker, value1],
	);

	const onChangeExcludeOverdue = useCallback(
		(value: OptionValue | null) => {
			if (!value) {
				return;
			}
			onChange({
				value1,
				value2,
				excludeOverdue: value.value === overdueOptions[1].value,
			});
		},
		[onChange, overdueOptions, value1, value2],
	);

	const optionValue2 = options.find((o) => o.value === value2);

	return (
		<StyledRow>
			<InputWrapper>
				<Input
					isInvalid={isInvalid}
					name="value1"
					onChange={onChangeValue1}
					value={value1}
					isCompact
					autoComplete="off"
					testId="jql-builder-basic-datetime.ui.time-input.input"
					aria-label={formatMessage(messages.timeInputLabel, {
						timeUnit: optionValue2?.label,
					})}
				/>
			</InputWrapper>
			<ChildWrapper overduePicker={overduePicker}>
				<Select
					value={optionValue2}
					aria-label={formatMessage(messages.labelTimeUnit)}
					onChange={onChangeValue2}
					name="value2"
					options={options}
					spacing="compact"
					components={Components}
					styles={{
						singleValue: (base) => ({
							...base,
							minWidth: 'fit-content',
						}),
					}}
					testId="jql-builder-basic-datetime.ui.time-input.time-units-select"
				/>
			</ChildWrapper>
			{overduePicker !== undefined && overduePicker === true && (
				<ChildWrapper>
					<Select
						value={excludeOverdue === true ? overdueOptions[1] : overdueOptions[0]}
						onChange={onChangeExcludeOverdue}
						name="excludeOverdue"
						options={overdueOptions}
						spacing="compact"
						components={Components}
						styles={{
							singleValue: (base) => ({
								...base,
								minWidth: 'fit-content',
							}),
						}}
						testId="jql-builder-basic-datetime.ui.time-input.overdue-select"
						aria-label={formatMessage(messages.labelTimeStatus)}
					/>
				</ChildWrapper>
			)}
		</StyledRow>
	);
};

export default TimeInput;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const InputWrapper = styled.div({
	flex: 0,
	minWidth: '72px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ChildWrapper = styled.div({
	flex: 0,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: (props: any) => (props.overduePicker ? '96px' : '152px'),
});
